import type { StateCreator } from 'zustand'

// Fila de toasts de confirmação (avisos leves, sem dependência externa).
// As ações que gravam/descarregam ficheiros ou aplicam overrides de
// posição empurram um toast curto por aqui; o ToastHost mostra a fila e
// o timeout remove-o sozinho. As mensagens inline ricas (ex.: o texto
// detalhado do editor de posição ou do alinhamento de federação)
// mantêm-se onde já existem; o toast é só a confirmação imediata.
//
// Chamável fora de componentes React via useAppStore.getState().pushToast,
// à semelhança dos outros overrides imperativos (setGeoOverride, etc.).

export type ToastType = 'success' | 'info' | 'error'

/** Um botão no toast (por exemplo "Abrir" depois de importar). */
export interface ToastAcao {
  rotulo: string
  executar: () => void
}

export interface Toast {
  id: string
  message: string
  type: ToastType
  timeout: number
  acao?: ToastAcao
}

export interface ToastOptions {
  type?: ToastType
  /** Milissegundos até remoção automática. 0 (ou negativo) mantém o
   *  toast até o utilizador o fechar. */
  timeout?: number
  /** Botão opcional; carregar nele executa e fecha o toast. */
  acao?: ToastAcao
}

export interface ToastSlice {
  toasts: Toast[]
  /** Empurra um toast para a fila e devolve o id (para remoção manual).
   *  Timeout por omissão: 4000 ms. */
  pushToast: (message: string, opts?: ToastOptions) => string
  dismissToast: (id: string) => void
}

export const DEFAULT_TOAST_TIMEOUT = 4000

let toastCounter = 0

export const createToastSlice: StateCreator<ToastSlice> = (set, get) => ({
  toasts: [],

  pushToast: (message, opts) => {
    const id = `t-${++toastCounter}-${Date.now().toString(36)}`
    const timeout = opts?.timeout ?? DEFAULT_TOAST_TIMEOUT
    const toast: Toast = {
      id,
      message,
      type: opts?.type ?? 'info',
      timeout,
      ...(opts?.acao ? { acao: opts.acao } : {}),
    }
    set((state) => ({ toasts: [...state.toasts, toast] }))
    if (timeout > 0) {
      setTimeout(() => get().dismissToast(id), timeout)
    }
    return id
  },

  dismissToast: (id) =>
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
})
